import { useCallback, useEffect, useState } from "react"
import { Editor as TiptapEditor } from "@tiptap/react"

interface UseEditorUIStateProps {
  /**
   * Ref holding the live TipTap editor instance. A ref (rather than the
   * instance itself) because this hook must run before useEditor — its
   * `highlightingEnabled` state feeds useEditorExtensions. The Editor
   * component assigns the ref during render, right after useEditor.
   */
  editorRef: React.RefObject<TiptapEditor | null>
  /** Setter for the non-collaborative editor content state */
  setEditorContent: React.Dispatch<React.SetStateAction<string>>
}

interface UseEditorUIStateReturn {
  highlightingEnabled: boolean
  lineNumbersEnabled: boolean
  elementHighlightingEnabled: boolean
  structurePanelVisible: boolean
  addElementModalVisible: boolean
  structureJumpToLine: number | undefined
  toggleHighlighting: () => void
  toggleLineNumbers: () => void
  toggleElementHighlighting: () => void
  toggleStructurePanel: () => void
  toggleAddElementModal: () => void
  setAddElementModalVisible: React.Dispatch<React.SetStateAction<boolean>>
  setStructureJumpToLine: React.Dispatch<
    React.SetStateAction<number | undefined>
  >
  jumpToStructure: (lineNumber: number) => void
}

/**
 * useEditorUIState owns the editor's presentation toggles (highlighting,
 * line numbers, element highlighting), the structure panel and add-element
 * modal visibility, the Ctrl+Shift+E shortcut, and structure-jump state.
 */
export const useEditorUIState = ({
  editorRef,
  setEditorContent,
}: UseEditorUIStateProps): UseEditorUIStateReturn => {
  const [highlightingEnabled, setHighlightingEnabled] = useState<boolean>(true)
  const [lineNumbersEnabled, setLineNumbersEnabled] = useState<boolean>(false)
  const [elementHighlightingEnabled, setElementHighlightingEnabled] =
    useState<boolean>(true)
  const [structurePanelVisible, setStructurePanelVisible] =
    useState<boolean>(false)
  const [addElementModalVisible, setAddElementModalVisible] =
    useState<boolean>(false)
  const [structureJumpToLine, setStructureJumpToLine] = useState<
    number | undefined
  >(undefined)

  /**
   * Toggles the highlighting feature in the editor.
   *
   * @function toggleHighlighting
   */
  const toggleHighlighting = (): void => {
    if (editorRef.current) {
      setEditorContent(editorRef.current.getHTML())
    }
    setHighlightingEnabled(!highlightingEnabled)
  }

  /**
   * Toggles the line numbers visibility in the editor.
   *
   * @function toggleLineNumbers
   */
  const toggleLineNumbers = (): void => {
    if (editorRef.current) {
      setEditorContent(editorRef.current.getHTML())
    }
    setLineNumbersEnabled(!lineNumbersEnabled)
  }

  /**
   * Toggles the element highlighting visibility in the editor.
   *
   * @function toggleElementHighlighting
   */
  const toggleElementHighlighting = (): void => {
    setElementHighlightingEnabled(!elementHighlightingEnabled)
  }

  /**
   * Toggles the structure panel visibility.
   *
   * @function toggleStructurePanel
   */
  const toggleStructurePanel = (): void => {
    setStructurePanelVisible(!structurePanelVisible)
  }

  /**
   * @function toggleAddElementModal
   */
  const toggleAddElementModal = (): void => {
    setAddElementModalVisible(!addElementModalVisible)
  }

  /**
   * Keyboard shortcut handler for Structure Panel (Ctrl+Shift+E)
   */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+Shift+E
      if (e.ctrlKey && e.shiftKey && e.key === "E") {
        e.preventDefault()
        setStructurePanelVisible((prev) => !prev)
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [])

  // Function to jump to structure at specific line
  const jumpToStructure = useCallback((lineNumber: number) => {
    setStructurePanelVisible(true)
    setStructureJumpToLine(lineNumber)
  }, [])

  return {
    highlightingEnabled,
    lineNumbersEnabled,
    elementHighlightingEnabled,
    structurePanelVisible,
    addElementModalVisible,
    structureJumpToLine,
    toggleHighlighting,
    toggleLineNumbers,
    toggleElementHighlighting,
    toggleStructurePanel,
    toggleAddElementModal,
    setAddElementModalVisible,
    setStructureJumpToLine,
    jumpToStructure,
  }
}
